import React, { useState, useRef, useEffect } from "react"
import { parseSSEStream, parseSSEFinal } from "../../utils/sse"
import StreamingMessage from "../CsrdBenchmarking/StreamingMessage"

// Advisor chat: streams /advisor/chat/stream through the proxy. Auth rides on
// authFetch's interceptor (Entra Bearer + username header for AAD sessions);
// the tenant is resolved server-side — this component never sends a company id.
const BASE_API = (process.env.REACT_APP_BASE_API_URL || "").replace(/\/+$/, "")

const MAX_MESSAGES = 100

const STARTER_QUESTIONS = [
  "What are my company's emissions?",
  "How complete is our sustainability data?",
  "What does market-based Scope 2 mean?",
  "Do we have emission reduction targets?",
]

const AdvisorChat = () => {
  const [messages, setMessages] = useState([])
  const [input, setInput] = useState("")
  const [isStreaming, setIsStreaming] = useState(false)
  const [sessionId, setSessionId] = useState(null)
  const messagesEndRef = useRef(null)
  const inputRef = useRef(null)
  const abortRef = useRef(null)

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" })
  }, [messages])

  // SSE parsing lives in utils/sse: only complete (blank-line terminated)
  // events are emitted, CRLF is normalised, and chunk whitespace is kept. The
  // hand-rolled parser this replaced split on "\n" only, so EcoUtil's CRLF
  // frames never terminated — every read re-emitted the whole answer so far
  // ("ScopeScope2 emissions are…") and trimmed the spaces between chunks.
  const applyEvent = (evt) => {
    if (evt.event === "session") {
      try {
        const parsed = JSON.parse(evt.data)
        if (parsed.session_id) setSessionId(parsed.session_id)
      } catch (e) { /* ignore */ }
      return
    }
    setMessages((prev) => {
      const updated = [...prev]
      const last = { ...updated[updated.length - 1] }

      switch (evt.event) {
        case "stage":
          try {
            const stage = JSON.parse(evt.data)
            if (stage.category) {
              last.classification = { category: stage.category, confidence: stage.confidence }
            }
          } catch (e) { /* ignore */ }
          break
        case "chunk":
          last.content += evt.data
          break
        case "sources":
          try {
            // SourceCard renders {type, path, preview}; the advisor sends {type, ref}.
            last.sources = JSON.parse(evt.data).map((s) => ({ type: s.type, path: s.ref, preview: s.preview }))
          } catch (e) { /* ignore */ }
          break
        case "error":
          last.content = last.content || evt.data || "The Advisor hit an error. Please try again."
          last.isStreaming = false
          break
        case "done":
          last.isStreaming = false
          break
        default:
          break
      }

      updated[updated.length - 1] = last
      return updated
    })
  }

  const failLastMessage = (text) => {
    setMessages((prev) => {
      const updated = [...prev]
      const last = { ...updated[updated.length - 1] }
      last.content = text
      last.isStreaming = false
      updated[updated.length - 1] = last
      return updated
    })
  }

  const askQuestion = async (query) => {
    if (!query.trim() || isStreaming) return

    const userMsg = { role: "user", content: query.trim() }
    const assistantMsg = { role: "assistant", content: "", classification: null, sources: [], isStreaming: true }

    setMessages((prev) => {
      const next = [...prev, userMsg, assistantMsg]
      return next.length > MAX_MESSAGES ? next.slice(-MAX_MESSAGES) : next
    })
    setInput("")
    setIsStreaming(true)

    if (abortRef.current) abortRef.current.abort()
    const ctrl = new AbortController()
    abortRef.current = ctrl

    try {
      const response = await fetch(`${BASE_API}/advisor/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          query: query.trim(),
          session_id: sessionId || undefined,
        }),
        signal: ctrl.signal,
      })

      if (!response.ok) {
        if (response.status === 401) {
          failLastMessage("The Advisor requires signing in with your organization (Microsoft) account.")
        } else if (response.status === 403) {
          failLastMessage("Your account doesn't have access to the Advisor.")
        } else {
          failLastMessage("The Advisor is unavailable right now. Please try again shortly.")
        }
        return
      }

      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ""

      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        buffer += decoder.decode(value, { stream: true })
        const { events, remainder } = parseSSEStream(buffer)
        buffer = remainder
        events.forEach(applyEvent)
      }
      // A final event may arrive without its terminator; parse it exactly once.
      parseSSEFinal(buffer).forEach(applyEvent)

      setMessages((prev) => {
        const updated = [...prev]
        const last = { ...updated[updated.length - 1] }
        last.isStreaming = false
        updated[updated.length - 1] = last
        return updated
      })
    } catch (error) {
      if (error.name === "AbortError") return
      console.error("Advisor error:", error)
      failLastMessage("Sorry, I couldn't process your question. Please try again.")
    } finally {
      setIsStreaming(false)
      inputRef.current?.focus()
    }
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    askQuestion(input)
  }

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault()
      askQuestion(input)
    }
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", backgroundColor: "#fff" }}>
      {/* Header */}
      <div style={{ padding: "16px 20px", borderBottom: "1px solid #E5E5E5", display: "flex", alignItems: "center", gap: "10px" }}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#407775" strokeWidth="2">
          <circle cx="12" cy="12" r="10" />
          <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
          <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
        <div>
          <h3 style={{ margin: 0, fontSize: "15px", fontWeight: "600", color: "#494949" }}>Advisor</h3>
          <p style={{ margin: 0, fontSize: "11px", color: "#707070" }}>
            Answers about your company's sustainability data, with sources
          </p>
        </div>
      </div>

      {/* Messages */}
      <div style={{ flex: 1, overflowY: "auto", padding: "20px", display: "flex", flexDirection: "column" }}>
        {messages.length === 0 && (
          <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", textAlign: "center", padding: "20px" }}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#407775" strokeWidth="1.5" style={{ marginBottom: "16px", opacity: 0.5 }}>
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
            <h4 style={{ color: "#494949", margin: "0 0 4px 0", fontSize: "16px" }}>EcoPrism Advisor</h4>
            <p style={{ color: "#707070", fontSize: "12px", margin: "0 0 20px 0", maxWidth: "300px" }}>
              Ask about your emissions, energy, water, data coverage, or what a
              regulation requires — answers cite your data and the regulatory texts.
            </p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", justifyContent: "center" }}>
              {STARTER_QUESTIONS.map((q, i) => (
                <button
                  key={i}
                  onClick={() => askQuestion(q)}
                  style={{ padding: "6px 14px", borderRadius: "16px", border: "1px solid #407775", backgroundColor: "transparent", color: "#407775", fontSize: "12px", cursor: "pointer", lineHeight: "1.3" }}
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((msg, i) => (
          <StreamingMessage
            key={i}
            message={msg}
            onFollowUp={null}
          />
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <form onSubmit={handleSubmit} style={{ padding: "12px 16px", borderTop: "1px solid #E5E5E5", backgroundColor: "#FAFAFA" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "8px", backgroundColor: "#fff", borderRadius: "24px", border: "1px solid #E5E5E5", padding: "4px 4px 4px 16px" }}>
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask about your sustainability data..."
            disabled={isStreaming}
            style={{ flex: 1, border: "none", outline: "none", fontSize: "13px", color: "#494949", backgroundColor: "transparent" }}
          />
          <button
            type="submit"
            disabled={!input.trim() || isStreaming}
            style={{ width: "32px", height: "32px", borderRadius: "50%", border: "none", backgroundColor: input.trim() && !isStreaming ? "#407775" : "#E5E5E5", color: "#fff", cursor: input.trim() && !isStreaming ? "pointer" : "default", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="22" y1="2" x2="11" y2="13" />
              <polygon points="22 2 15 22 11 13 2 9 22 2" />
            </svg>
          </button>
        </div>
      </form>
    </div>
  )
}

export default AdvisorChat
